import React from 'react'

import { FormattedMessage } from '@divvy-web/i18n'

import type {
  Collaborator,
  CollaboratorData,
  CollaboratorFields,
  CollaboratorFieldsOnCreditApp,
  CollaboratorMap,
} from './types'

export const getCollaboratorsOnCreditApplication = ({
  admin,
  authorizedSigner,
  beneficialOwners,
  outsourcedAccountant,
}: CollaboratorFieldsOnCreditApp): CollaboratorMap => {
  const uniqueCollaborators = new Map<string, CollaboratorData>()
  if (admin?.email) {
    uniqueCollaborators.set(admin.email, {
      firstName: admin.firstName,
      lastName: admin.lastName,
      role: (
        <FormattedMessage
          defaultMessage='Admin'
          id='sputnik.utils__iHN12u'
        />
      ),
    })
  }
  if (authorizedSigner?.email) {
    uniqueCollaborators.set(authorizedSigner?.email, {
      firstName: authorizedSigner.firstName,
      lastName: authorizedSigner.lastName,
      role: (
        <FormattedMessage
          defaultMessage='Authorized signer'
          id='sputnik.utils__2DRSsN'
        />
      ),
    })
  }
  beneficialOwners?.forEach((owner) => {
    if (owner?.email) {
      uniqueCollaborators.set(owner?.email, {
        firstName: owner.firstName,
        lastName: owner.lastName,
        role: (
          <FormattedMessage
            defaultMessage='Company owner'
            id='sputnik.utils__daQVrr'
          />
        ),
      })
    }
  })
  if (outsourcedAccountant?.email) {
    uniqueCollaborators.set(outsourcedAccountant?.email, {
      firstName: outsourcedAccountant.firstName,
      lastName: outsourcedAccountant.lastName,
      role: (
        <FormattedMessage
          defaultMessage='Outsourced accountant'
          id='sputnik.utils__h8O5Rm'
        />
      ),
    })
  }

  return uniqueCollaborators
}

export const getAllCollaborators = ({
  collaboratorsOnCreditApplication,
  email,
  creditApplication: { invitedUsersEmails },
}: {
  collaboratorsOnCreditApplication: CollaboratorMap
  email: string
  creditApplication: Pick<CollaboratorFields, 'invitedUsersEmails'>
}): Collaborator[] => {
  const uniqueCollaborators = new Set(collaboratorsOnCreditApplication.keys())

  invitedUsersEmails?.forEach((invitedUser) => {
    if (invitedUser && !uniqueCollaborators.has(invitedUser)) {
      uniqueCollaborators.add(invitedUser)
    }
  })

  // remove current user from the list
  uniqueCollaborators.delete(email)

  const uniqueCollaboratorsList = [...uniqueCollaborators.entries()]
  return uniqueCollaboratorsList.map(([email]) => {
    const userInfo = collaboratorsOnCreditApplication.get(email)

    return {
      email,
      firstName: userInfo?.firstName ?? null,
      lastName: userInfo?.lastName ?? null,
      role: userInfo?.role ?? null,
    }
  })
}
